/* eslint-disable */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, Redirect, useRouteMatch, useLocation } from 'react-router-dom';
import { TextStyled } from '@nike/nike-design-system-components';
import { nike } from '@nike/nike-design-tokens';
import { func, string } from 'prop-types';
import { Flex } from 'rebass';

import TableServerSideProvider from '~COMPONENTS/tableServerSide/TableServerSideContext';

import AccountSelectionAndDetail from './AccountSelectionAndDetail';
import AccountDepartments from './AccountDepartments/AccountDepartments';
import AccountCatalogs from './AccountCatalogs/AccountCatalogs';
import AccountContracts from './AccountContracts/AccountContracts';
import AccountAddresses from './AccountAddresses/AccountAddresses';

import UnderlineNavigation from '../../components/UnderlineNavigation';
import PageHeader from '../common/PageHeader';

import { getSelectedAccountId } from './selectors';

import { clearAdminAccount, fetchAdminAccounts } from './actions';
import { fetchCatalogTypes } from '../catalogTypes/actions';
import { fetchAllProductContractGroups } from '../productContracts/actions';

import { usePrevious } from '../../hooks/usePrevious';

import {
  columnFilterModifiersAccountDepartments,
  columnSortModifiersAccountDepartments
} from './AccountDepartments/accountDepartmentColumns';
import {
  columnFilterModifiersAccountCatalogs,
  columnSortModifiersAccountCatalogs
} from './AccountCatalogs/accountCatalogColumns';
import {
  columnFilterModifiersAccountContracts,
  columnSortModifiersAccountContracts
} from './AccountContracts/accountContractColumns';

const { podiumCdsColorGrey500 } = nike;

const AccountManagement = ({
  clearAdminAccount,
  fetchAdminAccounts,
  fetchCatalogTypes,
  fetchAllProductContractGroups,
  selectedAccountId
}) => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();

  const previousSelectedAccountId = usePrevious(selectedAccountId);

  useEffect(() => {
    fetchAdminAccounts();

    return () => {
      clearAdminAccount();
    };
  }, []);

  useEffect(() => {
    const fetchBulkData = async () => {
      await fetchAllProductContractGroups();
      await fetchCatalogTypes();
    };

    if (selectedAccountId) {
      fetchBulkData();
    }
  }, [selectedAccountId]);

  const isCatalogsTab = pathname.indexOf('catalogs') !== -1;
  const isContractsTab = pathname.indexOf('contracts') !== -1;
  const isAddressesTab = pathname.indexOf('addresses') !== -1;

  let initialActiveTab = 0; // defaults to department tab

  if (isCatalogsTab) {
    initialActiveTab = 1;
  } else if (isContractsTab) {
    initialActiveTab = 2;
  } else if (isAddressesTab) {
    initialActiveTab = 3;
  }

  return (
    <Flex flexDirection='column' width='100%' height='100%' px='10px'>
      <PageHeader title='Account Management' HeaderContent={<AccountSelectionAndDetail />} />
      {selectedAccountId && (
        <Flex justifyContent='space-between' my='10px'>
          <UnderlineNavigation
            list={[
              { href: `${url}/departments/${selectedAccountId}`, title: 'Departments' },
              { href: `${url}/catalogs/${selectedAccountId}`, title: 'Catalogs' },
              { href: `${url}/contracts/${selectedAccountId}`, title: 'Contracts' },
              { href: `${url}/addresses/${selectedAccountId}`, title: 'Addresses' }
            ]}
            initialActiveTab={initialActiveTab}
            reset={previousSelectedAccountId !== selectedAccountId}
          />
        </Flex>
      )}
      <Switch>
        <Route exact path={path}>
          {selectedAccountId ? (
            <Redirect
              to={{
                pathname: `${url}/departments/${selectedAccountId}`
              }}
            />
          ) : (
            <Flex alignItems='center' justifyContent='center' height='50%'>
              <TextStyled appearance='title4' color={podiumCdsColorGrey500}>
                Please select an account
              </TextStyled>
            </Flex>
          )}
        </Route>

        <Route exact path={`${path}/departments/:id`}>
          <TableServerSideProvider
            tableId='accountDepartments'
            columnSortModifiers={columnSortModifiersAccountDepartments}
            columnFilterModifiers={columnFilterModifiersAccountDepartments}
          >
            <AccountDepartments />
          </TableServerSideProvider>
        </Route>

        <Route exact path={`${path}/catalogs/:id`}>
          <TableServerSideProvider
            tableId='accountCatalogs'
            columnSortModifiers={columnSortModifiersAccountCatalogs}
            columnFilterModifiers={columnFilterModifiersAccountCatalogs}
          >
            <AccountCatalogs />
          </TableServerSideProvider>
        </Route>

        <Route exact path={`${path}/contracts/:id`}>
          <TableServerSideProvider
            tableId='accountContracts'
            columnSortModifiers={columnSortModifiersAccountContracts}
            columnFilterModifiers={columnFilterModifiersAccountContracts}
          >
            <AccountContracts />
          </TableServerSideProvider>
        </Route>

        <Route exact path={`${path}/addresses/:id`}>
          <AccountAddresses />
        </Route>
      </Switch>
    </Flex>
  );
};

AccountManagement.defaultProps = {
  selectedAccountId: null
};

AccountManagement.propTypes = {
  clearAdminAccount: func.isRequired,
  fetchAdminAccounts: func.isRequired,
  fetchAllProductContractGroups: func.isRequired,
  fetchCatalogTypes: func.isRequired,
  selectedAccountId: string
};

const mapStateToProps = state => ({ selectedAccountId: getSelectedAccountId(state) });

export default connect(mapStateToProps, {
  clearAdminAccount,
  fetchAllProductContractGroups,
  fetchAdminAccounts,
  fetchCatalogTypes
})(AccountManagement);
